import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { serverDomain } from "../../config";
import "../../styles/SettingsPage.css";

/* ---------- FL Modal ---------- */
const FlModal = ({ open, title, onClose, children, footer, width = 760 }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onEsc = (e) => e.key === "Escape" && onClose();
    const onDown = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) onClose();
    };

    document.addEventListener("keydown", onEsc);
    document.addEventListener("mousedown", onDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onEsc);
      document.removeEventListener("mousedown", onDown);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="flm-overlay" role="dialog" aria-label={title}>
      <div className="flm" ref={wrapRef} style={{ width: `min(${width}px, 96vw)` }}>
        <div className="flm__head">
          <div className="flm__title">{title}</div>
          <button className="flm__close" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        <div className="flm__body">{children}</div>

        {footer ? <div className="flm__footer">{footer}</div> : null}
      </div>
    </div>
  );
};

const methodLabel = (t) => {
  switch (t) {
    case "USDT_TRC20": return "USDT TRC20";
    case "USDT_BEP20": return "USDT BEP20";
    case "CRYPTO_BOT_CHECK": return "Crypto Bot Чек";
    case "IP_OOO": return "ИП/ООО";
    case "SELF_EMPLOYED": return "Самозанятый";
    default: return t || "Неизвестно";
  }
};

const currencyByType = (t) => (t === "USDT_TRC20" || t === "USDT_BEP20" || t === "CRYPTO_BOT_CHECK" ? "USD" : "RUB");

const walletPrimaryValue = (w) => {
  if (!w) return "—";
  if (w.type === "CRYPTO_BOT_CHECK") return w.telegram_user ? `@${w.telegram_user}` : "—";
  return w.usdt_wallet || w.fio || w.org_name || w.account_number || w.contract_number || "—";
};

const SettingsPage = () => {
  const navigate = useNavigate();

  const token = useMemo(() => localStorage.getItem("token"), []);
  const authOrRedirect = () => {
    if (!token) {
      navigate("/login");
      return false;
    }
    return true;
  };

  /* ---------- Profile ---------- */
  const [userData, setUserData] = useState({ name: "", email: "", telegram: "", phone: "" });
  const [profileLoading, setProfileLoading] = useState(false);

  /* ---------- Password ---------- */
  const [passwords, setPasswords] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [passwordError, setPasswordError] = useState("");

  /* ---------- Wallets ---------- */
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);

  const [showWalletModal, setShowWalletModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [walletData, setWalletData] = useState(null);

  const [paymentType, setPaymentType] = useState("USDT_TRC20");
  const [formData, setFormData] = useState({
    usdt_wallet: "",
    contract_number: "",
    telegram_user: "",
    contract_date: "",
    org_name: "",
    inn: "",
    kpp: "",
    account_number: "",
    bank_name: "",
    bik: "",
    fio: "",
    activity_type: "",
    phone: "",
    password: "",
  });

  /* ---------- Global postbacks ---------- */
  const [showGlobalPBModal, setShowGlobalPBModal] = useState(false);
  const [globalPB, setGlobalPB] = useState({ start: "", registration: "", subscription: "", payment: "" });
  const [mixedPB, setMixedPB] = useState({ start: false, registration: false, subscription: false, payment: false });
  const [overwriteAll, setOverwriteAll] = useState(false);
  const [showGlobalPBHelp, setShowGlobalPBHelp] = useState(false);

  const fetchProfile = async () => {
    if (!authOrRedirect()) return;
    setProfileLoading(true);
    try {
      const res = await fetch(`${serverDomain}/api/settings/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Ошибка загрузки профиля");
      setUserData(data.user || { name: "", email: "", telegram: "", phone: "" });
    } catch (e) {
      console.error(e);
      alert(e.message || "Ошибка загрузки профиля");
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchWallets = async () => {
    if (!authOrRedirect()) return;
    setWalletsLoading(true);
    try {
      const res = await fetch(`${serverDomain}/api/settings/wallets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      // у тебя API возвращает { wallets: [...] }
      setWallets(Array.isArray(data.wallets) ? data.wallets : Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setWalletsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Profile update ---------- */
  const handleProfileUpdate = async () => {
    if (!authOrRedirect()) return;
    try {
      const res = await fetch(`${serverDomain}/api/settings/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Ошибка обновления данных");
      alert("Данные успешно обновлены!");
      fetchProfile();
    } catch (e) {
      alert(e.message || "Ошибка");
    }
  };

  /* ---------- Password change ---------- */
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((p) => ({ ...p, [name]: value }));
  };

  const handleChangePassword = async () => {
    setPasswordError("");

    if (passwords.new_password !== passwords.confirm_password) {
      setPasswordError("Пароли не совпадают");
      return;
    }
    if (!authOrRedirect()) return;

    try {
      const res = await fetch(`${serverDomain}/api/settings/change-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(passwords),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Ошибка смены пароля");

      alert("Пароль успешно изменен!");
      setPasswords({ old_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      alert(e.message || "Ошибка");
    }
  };

  /* ---------- Wallet modal helpers ---------- */
  const resetWalletForm = () => {
    setFormData({
      usdt_wallet: "",
      contract_number: "",
      telegram_user: "",
      contract_date: "",
      org_name: "",
      inn: "",
      kpp: "",
      account_number: "",
      bank_name: "",
      bik: "",
      fio: "",
      activity_type: "",
      phone: "",
      password: "",
    });
  };

  const handleOpenWalletModal = () => {
    setIsEditing(false);
    setWalletData(null);
    setPaymentType("USDT_TRC20");
    resetWalletForm();
    setShowWalletModal(true);
  };

  const handleCloseWalletModal = () => {
    setShowWalletModal(false);
    setIsEditing(false);
    setWalletData(null);
    resetWalletForm();
  };

  const handleWalletChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleEditWallet = async (walletId) => {
    if (!authOrRedirect()) return;
    setIsEditing(true);

    try {
      const res = await fetch(`${serverDomain}/api/settings/wallets/${walletId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const wallet = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(wallet.error || "Не удалось загрузить кошелек");

      setWalletData(wallet);
      setPaymentType(wallet.type);

      const normalizedDate = wallet.contract_date ? String(wallet.contract_date).slice(0, 10) : "";

      setFormData({
        usdt_wallet: wallet.usdt_wallet || "",
        contract_number: wallet.contract_number || "",
        telegram_user: wallet.telegram_user ? `@${wallet.telegram_user}` : "",
        contract_date: normalizedDate,
        org_name: wallet.org_name || "",
        inn: wallet.inn || "",
        kpp: wallet.kpp || "",
        account_number: wallet.account_number || "",
        bank_name: wallet.bank_name || "",
        bik: wallet.bik || "",
        fio: wallet.fio || "",
        activity_type: wallet.activity_type || "",
        phone: wallet.phone || "",
        password: "",
      });

      setShowWalletModal(true);
    } catch (e) {
      alert(e.message || "Ошибка");
    }
  };

  const handleDeleteWallet = async (walletId) => {
    if (!authOrRedirect()) return;
    if (!window.confirm("Вы уверены, что хотите удалить этот кошелек?")) return;

    try {
      const res = await fetch(`${serverDomain}/api/settings/wallets/delete/${walletId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Ошибка удаления кошелька");

      alert("Кошелек успешно удален!");
      fetchWallets();
    } catch (e) {
      alert(e.message || "Ошибка");
    }
  };

  const handleSaveWallet = async () => {
    if (!authOrRedirect()) return;

    if (!formData.password) {
      alert("Текущий пароль обязателен");
      return;
    }

    let filteredData = { password: formData.password };
    let errorMessage = "";

    if (paymentType === "USDT_TRC20" || paymentType === "USDT_BEP20") {
      if (!formData.usdt_wallet) errorMessage = "Адрес USDT-кошелька не может быть пустым";
      else filteredData.usdt_wallet = formData.usdt_wallet;
    } else if (paymentType === "CRYPTO_BOT_CHECK") {
      const tg = (formData.telegram_user || "").trim();
      if (!tg) errorMessage = "Укажите Telegram username";
      else {
        const normalized = tg.startsWith("@") ? tg.slice(1) : tg;
        if (!/^[A-Za-z0-9_]{5,32}$/.test(normalized)) errorMessage = "Некорректный Telegram username";
        else filteredData.telegram_user = normalized;
      }
    } else if (paymentType === "IP_OOO") {
      if (!formData.contract_number) errorMessage = "№ договора обязателен";
      else if (!formData.contract_date) errorMessage = "Дата заключения договора обязательна";
      else if (!formData.org_name) errorMessage = "Название организации обязательно";
      else if (!formData.inn) errorMessage = "ИНН обязателен";
      else if (!formData.kpp) errorMessage = "КПП обязателен";
      else if (!formData.account_number) errorMessage = "Расчётный счёт обязателен";
      else if (!formData.bik) errorMessage = "БИК обязателен";
      else {
        filteredData = {
          ...filteredData,
          contract_number: formData.contract_number,
          contract_date: formData.contract_date,
          org_name: formData.org_name,
          inn: formData.inn,
          kpp: formData.kpp,
          account_number: formData.account_number,
          bank_name: formData.bank_name || null,
          bik: formData.bik,
        };
      }
    } else if (paymentType === "SELF_EMPLOYED") {
      if (!formData.fio) errorMessage = "ФИО обязательно";
      else if (!formData.inn) errorMessage = "ИНН обязателен";
      else if (!formData.activity_type) errorMessage = "Вид деятельности обязателен";
      else if (!formData.contract_number) errorMessage = "№ договора обязателен";
      else if (!formData.contract_date) errorMessage = "Дата заключения договора обязательна";
      else if (!formData.account_number) errorMessage = "Р/С обязателен";
      else if (!formData.bik) errorMessage = "БИК обязателен";
      else if (!formData.bank_name) errorMessage = "Имя банка обязательно";
      else if (!formData.phone) errorMessage = "Телефон обязателен";
      else {
        filteredData = {
          ...filteredData,
          fio: formData.fio,
          inn: formData.inn,
          activity_type: formData.activity_type,
          contract_number: formData.contract_number,
          contract_date: formData.contract_date,
          account_number: formData.account_number,
          bik: formData.bik,
          bank_name: formData.bank_name,
          phone: formData.phone,
        };
      }
    }

    if (errorMessage) {
      alert(errorMessage);
      return;
    }

    try {
      const method = isEditing ? "PUT" : "POST";
      const endpoint = isEditing
        ? `${serverDomain}/api/settings/wallets/edit/${walletData.id}`
        : `${serverDomain}/api/settings/wallets/add`;

      const res = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type: paymentType, ...filteredData }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Ошибка при сохранении кошелька");

      alert(isEditing ? "Кошелек успешно обновлен!" : "Кошелек успешно добавлен!");
      handleCloseWalletModal();
      fetchWallets();
    } catch (e) {
      alert(e.message || "Ошибка");
    }
  };

  /* ---------- Global PB ---------- */
  const loadGlobalPBSnapshot = async () => {
    if (!authOrRedirect()) return;
    try {
      const res = await fetch(`${serverDomain}/api/settings/postbacks/global_snapshot`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Не удалось получить глобальные постбеки");

      setGlobalPB({
        start: data.start?.mixed ? "" : (data.start?.value || ""),
        registration: data.registration?.mixed ? "" : (data.registration?.value || ""),
        subscription: data.subscription?.mixed ? "" : (data.subscription?.value || ""),
        payment: data.payment?.mixed ? "" : (data.payment?.value || ""),
      });
      setMixedPB({
        start: !!data.start?.mixed,
        registration: !!data.registration?.mixed,
        subscription: !!data.subscription?.mixed,
        payment: !!data.payment?.mixed,
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Ошибка");
    }
  };

  const openGlobalPBModal = async () => {
    setShowGlobalPBModal(true);
    setOverwriteAll(false);
    setShowGlobalPBHelp(false);
    await loadGlobalPBSnapshot();
  };

  const closeGlobalPBModal = () => {
    setShowGlobalPBModal(false);
    setGlobalPB({ start: "", registration: "", subscription: "", payment: "" });
    setMixedPB({ start: false, registration: false, subscription: false, payment: false });
    setOverwriteAll(false);
    setShowGlobalPBHelp(false);
  };

  const applyGlobalPostback = async () => {
    if (!authOrRedirect()) return;

    const allEmpty = Object.values(globalPB).every((v) => String(v ?? "").trim() === "");
    if (overwriteAll && allEmpty) {
      const ok = window.confirm(
        "Вы включили «Перезаписать» и оставили все поля пустыми.\n" +
          "Это очистит постбеки (NULL) у ВСЕХ ваших ссылок. Продолжить?"
      );
      if (!ok) return;
    }

    try {
      const res = await fetch(`${serverDomain}/api/settings/postbacks/apply_to_all`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...globalPB, overwrite: overwriteAll }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Ошибка применения глобального постбэка");
      alert(`Готово! Обновлено ссылок: ${data.affected}`);
      closeGlobalPBModal();
    } catch (e) {
      alert(e.message || "Ошибка");
    }
  };

  return (
    <div>
      <TopBar pageTitle={"Настройки"} />
      <div className="main-container">
        <SideBar />

        <main className="home-main flex-grow-1">
          <div className="fl-page fl-settings">
            {/* Profile */}
            <section className="fl-cardbox">
              <div className="fl-cardbox__head">
                <h2 className="fl-h2">Профиль</h2>
                <button className="fl-btn fl-btn--ghost" type="button" onClick={fetchProfile} disabled={profileLoading}>
                  Обновить
                </button>
              </div>

              <div className="fl-formGrid fl-formGrid--2">
                <label className="fl-labelBlock">
                  Имя
                  <input
                    className="fl-input fl-input--wide"
                    value={userData.name || ""}
                    onChange={(e) => setUserData((p) => ({ ...p, name: e.target.value }))}
                  />
                </label>

                <label className="fl-labelBlock">
                  Email
                  <input className="fl-input fl-input--wide" value={userData.email || ""} readOnly />
                </label>

                <label className="fl-labelBlock">
                  Telegram
                  <input
                    className="fl-input fl-input--wide"
                    value={userData.telegram || ""}
                    onChange={(e) => setUserData((p) => ({ ...p, telegram: e.target.value }))}
                  />
                </label>

                <label className="fl-labelBlock">
                  Телефон
                  <input
                    className="fl-input fl-input--wide"
                    value={userData.phone || ""}
                    onChange={(e) => setUserData((p) => ({ ...p, phone: e.target.value }))}
                  />
                </label>
              </div>

              <div className="fl-actionsRow">
                <button className="fl-btn" type="button" onClick={handleProfileUpdate}>
                  Сохранить изменения
                </button>
              </div>
            </section>

            {/* Wallets */}
            <section className="fl-cardbox">
              <div className="fl-cardbox__head">
                <h2 className="fl-h2">Платёжная информация</h2>
                <div className="fl-cardbox__headRight">
                  <button className="fl-btn fl-btn--ghost" type="button" onClick={fetchWallets} disabled={walletsLoading}>
                    Обновить
                  </button>
                  <button className="fl-btn" type="button" onClick={handleOpenWalletModal}>
                    Добавить кошелёк
                  </button>
                </div>
              </div>

              {walletsLoading ? (
                <div className="loading-container">
                  <div className="loader" />
                </div>
              ) : (
                <div className="fl-table-wrap">
                  <table className="fl-table fl-settings__table">
                    <thead>
                      <tr>
                        <th>Кошелёк</th>
                        <th className="fl-hide-sm">Система</th>
                        <th className="fl-hide-sm">Валюта</th>
                        <th style={{ width: 110 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {wallets.length > 0 ? (
                        wallets.map((w) => (
                          <tr key={w.id}>
                            <td className="fl-ellipsis" title={walletPrimaryValue(w)}>
                              {walletPrimaryValue(w)}
                            </td>
                            <td className="fl-hide-sm">{methodLabel(w.type)}</td>
                            <td className="fl-hide-sm">{currencyByType(w.type)}</td>
                            <td className="fl-actionsCell">
                              <button className="fl-iconbtn fl-iconbtn--edit" type="button" onClick={() => handleEditWallet(w.id)} title="Редактировать">
                                ⚙️
                              </button>
                              <button className="fl-iconbtn fl-iconbtn--trash" type="button" onClick={() => handleDeleteWallet(w.id)} title="Удалить">
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} style={{ textAlign: "center", padding: 16, opacity: 0.7 }}>
                            Нет данных
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Global postback */}
            <section className="fl-cardbox">
              <div className="fl-cardbox__head">
                <h2 className="fl-h2">Глобальный PostBack</h2>
                <button className="fl-btn" type="button" onClick={openGlobalPBModal}>
                  Открыть настройки
                </button>
              </div>

              <div className="fl-note">
                Можно применить PostBack ко всем ссылкам. Если «Перезаписать» выключено — заполнятся только пустые поля (NULL).
              </div>
            </section>

            {/* Password */}
            <section className="fl-cardbox">
              <div className="fl-cardbox__head">
                <h2 className="fl-h2">Смена пароля</h2>
              </div>

              <div className="fl-formGrid fl-formGrid--2">
                <label className="fl-labelBlock">
                  Текущий пароль
                  <input
                    className="fl-input fl-input--wide"
                    type="password"
                    name="old_password"
                    value={passwords.old_password}
                    onChange={handlePasswordChange}
                  />
                </label>

                <label className="fl-labelBlock">
                  Новый пароль
                  <input
                    className="fl-input fl-input--wide"
                    type="password"
                    name="new_password"
                    value={passwords.new_password}
                    onChange={handlePasswordChange}
                  />
                </label>

                <label className="fl-labelBlock">
                  Повторите новый пароль
                  <input
                    className="fl-input fl-input--wide"
                    type="password"
                    name="confirm_password"
                    value={passwords.confirm_password}
                    onChange={handlePasswordChange}
                  />
                </label>

                <div />
              </div>

              {passwordError ? <div className="fl-alert fl-alert--error">{passwordError}</div> : null}

              <div className="fl-actionsRow">
                <button className="fl-btn" type="button" onClick={handleChangePassword} disabled={!passwords.new_password}>
                  Сменить пароль
                </button>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Wallet modal */}
      <FlModal
        open={showWalletModal}
        title={isEditing ? "Редактировать кошелёк" : "Добавить кошелёк"}
        onClose={handleCloseWalletModal}
        width={860}
        footer={
          <div className="flm__footerRow">
            <button className="fl-btn fl-btn--ghost" type="button" onClick={handleCloseWalletModal}>
              Отмена
            </button>
            <button className="fl-btn" type="button" onClick={handleSaveWallet}>
              Сохранить
            </button>
          </div>
        }
      >
        <div className="fl-formGrid">
          <label className="fl-labelBlock">
            Платёжная система
            <select className="fl-select" value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
              <option value="USDT_TRC20">USDT TRC20 (USD) — Комиссия: 4$</option>
              <option value="USDT_BEP20">USDT BEP20 (USD) — Комиссия: 2$</option>
              <option value="CRYPTO_BOT_CHECK">Crypto Bot Чек (USD) — Без комиссии</option>
              <option value="IP_OOO">ИП или ООО (RUB) — Без комиссии</option>
              <option value="SELF_EMPLOYED">Самозанятый (RUB) — Комиссия: 1%</option>
            </select>
          </label>

          {(paymentType === "USDT_TRC20" || paymentType === "USDT_BEP20") && (
            <label className="fl-labelBlock">
              USDT {paymentType === "USDT_TRC20" ? "TRC20" : "BEP20"} Wallet
              <input className="fl-input fl-input--wide" name="usdt_wallet" value={formData.usdt_wallet} onChange={handleWalletChange} />
            </label>
          )}

          {paymentType === "CRYPTO_BOT_CHECK" && (
            <label className="fl-labelBlock">
              Telegram username (получатель чека)
              <input
                className="fl-input fl-input--wide"
                name="telegram_user"
                placeholder="@username"
                value={formData.telegram_user}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  const withAt = v.startsWith("@") ? v : "@" + v;
                  setFormData((p) => ({ ...p, telegram_user: withAt }));
                }}
              />
            </label>
          )}

          {paymentType === "IP_OOO" && (
            <div className="fl-formGrid fl-formGrid--2">
              <label className="fl-labelBlock">
                № договора
                <input className="fl-input" name="contract_number" value={formData.contract_number} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Дата заключения договора
                <input className="fl-input" type="date" name="contract_date" value={formData.contract_date} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Организация / ФИО
                <input className="fl-input" name="org_name" value={formData.org_name} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                ИНН
                <input className="fl-input" name="inn" value={formData.inn} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                КПП
                <input className="fl-input" name="kpp" value={formData.kpp} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Счёт получателя
                <input className="fl-input" name="account_number" value={formData.account_number} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                БИК
                <input className="fl-input" name="bik" value={formData.bik} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Банк (опц.)
                <input className="fl-input" name="bank_name" value={formData.bank_name} onChange={handleWalletChange} />
              </label>
            </div>
          )}

          {paymentType === "SELF_EMPLOYED" && (
            <div className="fl-formGrid fl-formGrid--2">
              <label className="fl-labelBlock">
                ФИО
                <input className="fl-input" name="fio" value={formData.fio} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                ИНН
                <input className="fl-input" name="inn" value={formData.inn} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Вид деятельности
                <input className="fl-input" name="activity_type" value={formData.activity_type} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                № договора
                <input className="fl-input" name="contract_number" value={formData.contract_number} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Дата договора
                <input className="fl-input" type="date" name="contract_date" value={formData.contract_date} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Р/С
                <input className="fl-input" name="account_number" value={formData.account_number} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                БИК
                <input className="fl-input" name="bik" value={formData.bik} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Имя банка
                <input className="fl-input" name="bank_name" value={formData.bank_name} onChange={handleWalletChange} />
              </label>
              <label className="fl-labelBlock">
                Телефон
                <input className="fl-input" name="phone" value={formData.phone} onChange={handleWalletChange} />
              </label>
            </div>
          )}

          <label className="fl-labelBlock">
            Текущий пароль
            <input className="fl-input fl-input--wide" type="password" name="password" value={formData.password} onChange={handleWalletChange} />
          </label>

          <div className="fl-note">
            Пароль нужен для подтверждения добавления/редактирования платёжных реквизитов.
          </div>
        </div>
      </FlModal>

      {/* Global PB modal */}
      <FlModal
        open={showGlobalPBModal}
        title="Глобальный PostBack"
        onClose={closeGlobalPBModal}
        width={860}
        footer={
          <div className="flm__footerRow">
            <button className="fl-btn fl-btn--ghost" type="button" onClick={closeGlobalPBModal}>
              Отмена
            </button>
            <button className="fl-btn" type="button" onClick={applyGlobalPostback}>
              Применить ко всем
            </button>
          </div>
        }
      >
        <div className="fl-formGrid">
          <div className="fl-formGrid fl-formGrid--2">
            <label className="fl-labelBlock">
              URL Старта
              <input
                className="fl-input fl-input--wide"
                value={globalPB.start}
                placeholder={mixedPB.start ? "Разные значения — у ссылок разные URL старта" : "http://example.com/start.php?clickid={clickid}"}
                onChange={(e) => setGlobalPB((p) => ({ ...p, start: e.target.value }))}
              />
              {mixedPB.start ? <span className="fl-hint">Сейчас у разных ссылок стоят разные URL старта.</span> : null}
            </label>

            <label className="fl-labelBlock">
              URL Регистрация
              <input
                className="fl-input fl-input--wide"
                value={globalPB.registration}
                placeholder={mixedPB.registration ? "Разные значения — у ссылок разные URL регистрации" : "http://example.com/registration.php?clickid={clickid}"}
                onChange={(e) => setGlobalPB((p) => ({ ...p, registration: e.target.value }))}
              />
              {mixedPB.registration ? <span className="fl-hint">Сейчас у разных ссылок стоят разные URL регистрации.</span> : null}
            </label>

            <label className="fl-labelBlock">
              URL Подписки (Списания)
              <input
                className="fl-input fl-input--wide"
                value={globalPB.subscription}
                placeholder={mixedPB.subscription ? "Разные значения — у ссылок разные URL подписок" : "http://example.com/trial.php?clickid={clickid}&payment={payment}"}
                onChange={(e) => setGlobalPB((p) => ({ ...p, subscription: e.target.value }))}
              />
              {mixedPB.subscription ? <span className="fl-hint">Сейчас у разных ссылок стоят разные URL подписок.</span> : null}
            </label>

            <label className="fl-labelBlock">
              URL Покупки (Первая покупка)
              <input
                className="fl-input fl-input--wide"
                value={globalPB.payment}
                placeholder={mixedPB.payment ? "Разные значения — у ссылок разные URL покупок" : "http://example.com/pay.php?clickid={clickid}&payment={payment}"}
                onChange={(e) => setGlobalPB((p) => ({ ...p, payment: e.target.value }))}
              />
              {mixedPB.payment ? <span className="fl-hint">Сейчас у разных ссылок стоят разные URL покупок.</span> : null}
            </label>
          </div>

          <label className="fl-check fl-check--block">
            <input type="checkbox" checked={overwriteAll} onChange={(e) => setOverwriteAll(e.target.checked)} />
            <span>Перезаписать, даже если у ссылки уже задан постбек</span>
          </label>

          <button className="fl-linklike" type="button" onClick={() => setShowGlobalPBHelp((v) => !v)}>
            {showGlobalPBHelp ? "Скрыть инструкцию" : "Как настроить PostBack"}
          </button>

          {showGlobalPBHelp ? (
            <pre className="fl-helpbox">
                {
                    'Макросы для постбэка:\n{clickid} - уникальный идентификатор клика.\n{payment} - сумма выплаты.\n\n'
                    + 'Если используете трекер, то ссылка будет выглядеть таким образом:\nhttps://t.me/fastlovetg_bot?start=TEST-s-{clickid}\n\n'
                    + 'Вместо TEST - название созданного потока\n-s- - обязательный параметр перед {clickid}\n\n'
                    + 'Если вы хотите использовать постбэк в Telegram:\nhttps://api.telegram.org/bot(API TOKEN)/sendMessage?chat_id=(user_id)&text={payment}\n\n'
                    + 'Вместо (API TOKEN) - указать токен вашего бота.\nВместо (user_id) - указать ID личного аккаунта, чата - куда бот будет отправлять запрос.'
                }
            </pre>
          ) : null}

          <div className="fl-note">
            • Указывайте только те поля, которые хотите применить сейчас. <br />
            • Если «Перезаписать» выключено — значения применятся только там, где сейчас пусто (NULL). <br />
            • С «Перезаписать»: пустая строка очистит поле (NULL) у всех ссылок.
          </div>
        </div>
      </FlModal>
    </div>
  );
};

export default SettingsPage;